import { useBanners } from '@databases/banner/useBanners';
import { setView as setViewInState } from '@databases/database-state/state/view';
import { isStandalone } from '@databases/modes';
import { defaultStoreUtils } from '@databases/redux';
import {
  type DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS,
  isBuiltInDatabaseLayoutType,
  ViewId
} from '@databases/types/database/view';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import {
  applyViewChange,
  getDefaultView,
  type ViewChangeType,
  ViewChangeTypes
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';

import {
  initialLayoutSettings,
  type LayoutSettingsInterface,
  ViewLoader
} from '../components/ViewLoader';
import { ViewPanelSettingType, type NavigationOptions } from '../types/types';

const DEFAULT_VIEW_ID = '';

export const resolveViewChangeListId = (
  viewId: string | null,
  fallbackViewId: string,
  databaseDefaultViewId: string | null
): string | null =>
  viewId ?? (fallbackViewId === DEFAULT_VIEW_ID ? databaseDefaultViewId : fallbackViewId);

const DEFAULT_VIEW: DatabaseView = {
  id: DEFAULT_VIEW_ID,
  name: '',
  settings: DEFAULT_DATABASE_VIEW_SETTINGS
};

export interface ViewContextInterface {
  view: DatabaseView;
  unsavedView: DatabaseView | null;
  resolvedView: DatabaseView;
  setViewChange: (delta: ViewChangeType | ViewChangeType[]) => void;
  getViewChangeList: () => ViewChangeType[];
  resetViewChangeList: (viewId: string) => void;
  setViewId: (viewId: string) => void;
  activePanelSetting: ViewPanelSettingType | null;
  setActivePanelSetting: (
    setting: ViewPanelSettingType | null,
    options?: NavigationOptions
  ) => void;
  previousPanelSetting: ViewPanelSettingType;
  clearUnsavedView: () => void;
  onUnsavedViewLoaded?: () => void;
  /**
   * Maps view IDs to their unsaved (pending) name changes.
   * Used to show updated names for views that have been renamed
   * but not yet saved (e.g. in tabs, overflow picker, and validation).
   */
  pendingViewNames: Record<string, string>;
}

export const ViewContext = createContext<ViewContextInterface>({
  view: DEFAULT_VIEW,
  unsavedView: null,
  resolvedView: DEFAULT_VIEW,
  setViewChange: () => {},
  getViewChangeList: () => [],
  resetViewChangeList: () => {},
  setViewId: () => {},
  activePanelSetting: null,
  setActivePanelSetting: () => {},
  previousPanelSetting: ViewPanelSettingType.OVERVIEW,
  clearUnsavedView: () => {},
  pendingViewNames: {}
});

export interface ViewProviderProps {
  children: React.ReactNode;
  urlSavedViewId: string | null;
  initialUnsavedViewId: string | null;
  contentId: string;
  onUnsavedViewLoaded?: () => void;
}

export const ViewProvider = ({
  children,
  urlSavedViewId,
  initialUnsavedViewId,
  contentId,
  onUnsavedViewLoaded
}: ViewProviderProps) => {
  const databaseDefaultViewId = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdOptional(state, {
      contentId: contentId
    });

    if (!database || isBrokenDatabase(database)) {
      return null;
    }

    const defaultView = getDefaultView(database);

    return defaultView ? defaultView.id : null;
  });

  const [viewChangeLists, setViewChangeLists] = useState<
    Record<string, ViewChangeType[]>
  >({});
  const [activePanelSetting, setActivePanelSettingState] =
    useState<ViewPanelSettingType | null>(null);
  const [previousPanelSetting, setPreviousPanelSetting] = useState<ViewPanelSettingType>(
    ViewPanelSettingType.OVERVIEW
  );

  const setActivePanelSetting = useCallback(
    (setting: ViewPanelSettingType | null, options?: NavigationOptions) => {
      // Set navigation context based on explicit 'from' option, otherwise default to Overview
      if (setting !== null && options?.from) {
        setPreviousPanelSetting(options.from);
      } else {
        setPreviousPanelSetting(ViewPanelSettingType.OVERVIEW);
      }

      setActivePanelSettingState(setting);
    },
    []
  );

  const [unsavedViewId, setUnsavedViewId] = useState<string | null>(initialUnsavedViewId);
  const [viewId, setViewId] = useState<string | null>(
    urlSavedViewId ?? databaseDefaultViewId
  );
  const [resolvedView, setResolvedView] = useState<DatabaseView>(DEFAULT_VIEW);

  const [view, setView] = useState<DatabaseView>(DEFAULT_VIEW);
  const [unsavedView, setUnsavedView] = useState<DatabaseView | null>(null);
  const [layoutSettings, setLayoutSettings] = useState<
    Record<string, LayoutSettingsInterface>
  >({});

  const lastViewedContentId = useRef(contentId);

  const { addBanner } = useBanners();

  useEffect(() => {
    if (initialUnsavedViewId) {
      if (urlSavedViewId === null || urlSavedViewId === ViewId.UNSAVED) {
        setViewId(ViewId.UNSAVED);
      }
      setUnsavedViewId(initialUnsavedViewId);
    }
  }, [urlSavedViewId, initialUnsavedViewId]);

  useEffect(() => {
    if (contentId !== lastViewedContentId.current) {
      setViewChangeLists({});
      setViewId(urlSavedViewId ?? databaseDefaultViewId);
      lastViewedContentId.current = contentId;
    }
  }, [contentId, lastViewedContentId, urlSavedViewId, databaseDefaultViewId]);

  const resolveChangeListId = useCallback(
    (fallbackViewId: string) =>
      resolveViewChangeListId(viewId, fallbackViewId, databaseDefaultViewId),
    [databaseDefaultViewId, viewId]
  );

  const processViewChange = useCallback(
    (baseView: DatabaseView, delta: ViewChangeType) => {
      // if it's a ViewChangeTypes.LAYOUT_TYPE change
      // we need to maintain other settings for this view
      // so our base view need to get his previous state back (maintained in layoutSettings)

      const currentViewId = viewId !== null ? viewId : databaseDefaultViewId;
      if (
        delta.type === ViewChangeTypes.LAYOUT_TYPE &&
        currentViewId &&
        isBuiltInDatabaseLayoutType(delta.value)
      ) {
        baseView = {
          ...view,
          settings: {
            ...view.settings,
            layout: layoutSettings[currentViewId][delta.value]
          }
        };
      }

      return applyViewChange(baseView, delta);
    },
    [databaseDefaultViewId, layoutSettings, view, viewId]
  );

  const setViewChange = useCallback(
    (delta: ViewChangeType | ViewChangeType[]) => {
      if (!Array.isArray(delta)) {
        delta = [delta];
      }

      let newView = view;
      delta.forEach((change) => {
        newView = processViewChange(newView, change);
      });

      setLayoutSettings((prev) => {
        const layoutType = newView.settings.layout.type;

        if (!isBuiltInDatabaseLayoutType(layoutType)) {
          return prev;
        }

        return {
          ...prev,
          [newView.id]: {
            ...prev[newView.id],
            [layoutType]: newView.settings.layout
          }
        };
      });
      setView(newView);

      const targetViewId = resolveChangeListId(view.id);
      if (targetViewId) {
        setViewChangeLists((prevViewChangeLists) => {
          return {
            ...prevViewChangeLists,
            [targetViewId]: [...(prevViewChangeLists[targetViewId] ?? []), ...delta]
          };
        });
      }

      const nonSavedViewChangeTypes: ViewChangeType['type'][] = [
        ViewChangeTypes.VIEW_SEARCH
      ];

      // These changes are not persisted with the view when saving it, so only trigger the
      // banner if some of the changes in the delta are *not* excluded types
      if (delta.some((d) => !nonSavedViewChangeTypes.includes(d.type))) {
        addBanner('unsaved-view-changes');
      }
    },
    [addBanner, processViewChange, resolveChangeListId, view]
  );

  const currentView = useMemo(() => {
    const layoutSettingsForView = layoutSettings[view.id];
    const layoutType = view.settings.layout.type;
    const builtInLayoutType = isBuiltInDatabaseLayoutType(layoutType)
      ? layoutType
      : undefined;
    const layoutForType = layoutSettingsForView
      ? builtInLayoutType
        ? layoutSettingsForView[builtInLayoutType]
        : null
      : null;
    const newViewState = {
      ...view,
      settings: {
        ...view.settings,
        layout:
          layoutForType ??
          (builtInLayoutType
            ? initialLayoutSettings[builtInLayoutType]
            : view.settings.layout)
      }
    };
    setViewInState(newViewState);

    return newViewState;
  }, [layoutSettings, view]);

  const addViewIdToURL = useCallback((newViewId: string) => {
    if (isStandalone()) {
      const parentWindow = window.parent;
      const urlParams = new URLSearchParams(parentWindow.location.search);
      urlParams.set('savedViewId', newViewId);
      parentWindow.history.replaceState(
        {},
        '',
        `${parentWindow.location.pathname}?${urlParams}`
      );
    }
  }, []);

  const resetViewSearch = useCallback(() => {
    if (viewId) {
      setViewChangeLists((prevViewChangeLists) => {
        return {
          ...prevViewChangeLists,
          [viewId]: [
            ...(prevViewChangeLists[viewId]?.filter(
              (change) => change.type !== ViewChangeTypes.VIEW_SEARCH
            ) ?? [])
          ]
        };
      });
    }
  }, [viewId]);

  const onSetViewId = useCallback(
    (newViewId: string) => {
      resetViewSearch();
      setViewId(newViewId);
      addViewIdToURL(newViewId);
    },
    [addViewIdToURL, resetViewSearch]
  );

  const clearUnsavedView = useCallback(() => {
    setUnsavedView(null);
  }, []);

  const resetViewChangeList = useCallback(
    (resetViewId: string) => {
      const targetViewId = resolveChangeListId(resetViewId);

      setViewChangeLists((prevViewChangeLists) => {
        return {
          ...prevViewChangeLists,
          [targetViewId ?? resetViewId]: []
        };
      });
    },
    [resolveChangeListId]
  );

  const currentViewChangeList = useMemo(() => {
    const targetViewId = resolveChangeListId(view.id);
    return targetViewId ? (viewChangeLists[targetViewId] ?? []) : [];
  }, [resolveChangeListId, view.id, viewChangeLists]);

  const pendingViewNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const [id, changes] of Object.entries(viewChangeLists)) {
      // Find the last VIEW_NAME change for each view
      for (let i = changes.length - 1; i >= 0; i--) {
        const change = changes[i];
        if (change.type === ViewChangeTypes.VIEW_NAME) {
          names[id] = change.value;
          break;
        }
      }
    }
    return names;
  }, [viewChangeLists]);

  const contextValue = useMemo(
    () => ({
      view: currentView,
      unsavedView,
      setViewChange,
      getViewChangeList: () => currentViewChangeList,
      resetViewChangeList,
      activePanelSetting,
      setActivePanelSetting,
      previousPanelSetting,
      setViewId: onSetViewId,
      resolvedView,
      clearUnsavedView,
      onUnsavedViewLoaded,
      pendingViewNames
    }),
    [
      currentView,
      unsavedView,
      setViewChange,
      currentViewChangeList,
      resetViewChangeList,
      activePanelSetting,
      setActivePanelSetting,
      previousPanelSetting,
      onSetViewId,
      resolvedView,
      clearUnsavedView,
      onUnsavedViewLoaded,
      pendingViewNames
    ]
  );

  return (
    <ViewContext.Provider value={contextValue}>
      <ViewLoader
        contentId={contentId}
        setView={setView}
        viewId={viewId}
        urlSavedViewId={urlSavedViewId}
        unsavedViewId={unsavedViewId}
        unsavedView={unsavedView}
        viewChangeList={currentViewChangeList}
        setUnsavedView={setUnsavedView}
        setResolvedView={setResolvedView}
        setLayoutSettings={setLayoutSettings}
        onUnsavedViewLoaded={onUnsavedViewLoaded}
      />
      {children}
    </ViewContext.Provider>
  );
};
